import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  type Business,
  type ModuleId,
  type Role,
  type Session,
  type User,
  can as canDo,
  grantSet,
  type Action,
  type Subject,
} from '@prody/shared';
import { createFormatters, type Formatters } from '../format/formatters';

/* ------------------------------------------------------------------ *
 * Tenancy
 *
 * The active business is derived from the URL (`/b/:slug/...`), not
 * from a store. Three things fall out of that, and they are the reason
 * it is done this way:
 *
 *   - switching tenant is a navigation, so TanStack Query's cache keys
 *     (which all include the business id) invalidate naturally instead
 *     of needing a manual purge;
 *   - two tenants can be open in two browser tabs, which the owner of
 *     a wholesaler and its farm will do on day one;
 *   - a link pasted into WhatsApp opens the right tenant for whoever
 *     clicks it.
 * ------------------------------------------------------------------ */

export interface BusinessContextValue {
  business: Business;
  user: User;
  role: Role;
  format: Formatters;
  /** Expanded once per membership, then read on every render. */
  permissions: ReadonlySet<string>;
  can: (subject: Subject, action: Action) => boolean;
  hasModule: (id: ModuleId) => boolean;
  /** Every other business this user can switch to. */
  otherBusinesses: Business[];
}

const BusinessContext = createContext<BusinessContextValue | null>(null);

export function BusinessProvider({
  session,
  businessSlug,
  children,
}: {
  session: Session;
  businessSlug: string;
  children: ReactNode;
}) {
  const value = useMemo<BusinessContextValue | null>(() => {
    const membership = session.memberships.find((m) => m.business.slug === businessSlug);
    if (!membership) return null;

    const permissions = grantSet({
      role: membership.role,
      extraPermissions: membership.extraPermissions,
    });
    const enabled = new Set(membership.business.enabledModules);

    return {
      business: membership.business,
      user: session.user,
      role: membership.role,
      format: createFormatters({ settings: membership.business.settings }),
      permissions,
      can: (subject, action) => canDo(permissions, subject, action),
      hasModule: (id) => enabled.has(id),
      otherBusinesses: session.memberships
        .filter((m) => m.business.slug !== businessSlug)
        .map((m) => m.business),
    };
  }, [session, businessSlug]);

  if (!value) {
    throw new Error(`No membership for business "${businessSlug}"`);
  }

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusiness(): BusinessContextValue {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used inside a BusinessProvider');
  return ctx;
}

/** Tenant-scoped formatters. The only sanctioned way to render a number or a date. */
export function useFormat(): Formatters {
  return useBusiness().format;
}

/** `const canEditPrices = useCan('price', 'update')` */
export function useCan(subject: Subject, action: Action): boolean {
  const { can } = useBusiness();
  return can(subject, action);
}
